import { useState } from 'react';

/**
 * Custom hook för att hantera feedback och konsultsvar för offert
 * 
 * @param {Object} offert - Offert-objektet
 * @param {Function} setFeedbackThread - Funktion för att uppdatera feedbackThread
 * @param {Function} fetchFeedbackThread - Funktion för att hämta feedback-tråd
 * @param {Function} fetchFeedbackHistory - Funktion för att hämta feedback-historik från alla versioner
 * @param {Function} fetchIntelligentStatus - Funktion för att hämta intelligent status
 * @param {Function} onSuccess - Callback för att visa success modal
 * @param {Function} onError - Callback för att visa error modal
 * @param {String} viewMode - 'consultant' eller 'customer'
 * @returns {Object} - State och funktioner för feedback-hantering
 */
const useOffertFeedback = (offert, setFeedbackThread, fetchFeedbackThread, fetchFeedbackHistory, fetchIntelligentStatus, onSuccess, onError, viewMode = 'consultant') => {
  // State
  const [consultantResponse, setConsultantResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [expandedFeedbackItems, setExpandedFeedbackItems] = useState({});
  const [newReplies, setNewReplies] = useState({});
  const [savedReplies, setSavedReplies] = useState({});

  // Toast notification helper (använder det globala toast-systemet)
  const showToast = (message, type = 'success') => {
    const event = new CustomEvent('show-toast', {
      detail: { message, type, duration: 3000 }
    });
    window.dispatchEvent(event);
  };

  // Skicka konsultsvar på feedback
  const handleSendConsultantResponse = async () => {
    if (!consultantResponse.trim() || !offert?._id) return;
    
    setSendingResponse(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/consultant-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response: consultantResponse.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte skicka svar');
      }

      const data = await response.json();
      
      if (data.success) {
        // Uppdatera tråden och status
        setFeedbackThread(data.thread);
        setConsultantResponse('');
        await fetchIntelligentStatus();
        
        // Visa framgångsmeddelande
        const isMarkAsOK = consultantResponse.trim() === '✓ Markerad som OK';
        if (onSuccess) {
          onSuccess({
            title: isMarkAsOK ? 'Markerad som OK!' : 'Svar skickat!',
            message: isMarkAsOK ? 
              'Feedbacken har markerats som OK.' : 
              'Ditt svar har skickats till kunden. Kunden har fått ett e-postmeddelande.',
            type: 'success'
          });
        }
      } else {
        throw new Error(data.message || 'Kunde inte skicka svar');
      }
    } catch (err) {
      console.error('❌ Fel vid skickande av konsultsvar:', err);
      if (onError) {
        onError({
          title: 'Fel uppstod',
          message: err.message,
          type: 'error'
        });
      }
    } finally {
      setSendingResponse(false);
    }
  };

  // Spara svar lokalt (för tråd-funktionalitet)
  const handleSaveReply = (commentKey) => {
    const replyText = newReplies[commentKey];
    if (!replyText?.trim()) return;
    
    setSavedReplies(prev => ({
      ...prev,
      [commentKey]: replyText.trim()
    }));
    
    // Rensa textfältet
    setNewReplies(prev => ({
      ...prev,
      [commentKey]: ''
    }));
  };

  // Skicka alla sparade svar
  const handleSendAllReplies = async () => {
    if (Object.keys(savedReplies).length === 0) return;
    
    setSendingResponse(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Bestäm endpoint baserat på viewMode
      const endpoint = viewMode === 'customer' ? 'customer-reply' : 'consultant-response';
      
      // 🔧 Skicka ALLA svar i EN request (responses-array). Backend lägger varje
      // svar som eget trådmeddelande men skickar bara ETT mail + EN notis till
      // kunden — inte ett per svar. Kolla även res.ok (tidigare ignorerades
      // HTTP-fel helt, så misslyckade utskick visade ändå falsk success).
      const responses = Object.values(savedReplies)
        .map(r => (r || '').trim())
        .filter(Boolean);

      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ responses })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Kunde inte skicka svar till kund');
      }
      
      // Uppdatera tråden, historiken och status
      await fetchFeedbackThread();
      await fetchFeedbackHistory();
      await fetchIntelligentStatus();
      
      // Rensa sparade svar OCH textfältet så det inte ligger kvar nästa
      // gång feedback-sektionen öppnas
      setSavedReplies({});
      setNewReplies({});
      
      // Visa framgångsmeddelande baserat på viewMode
      if (onSuccess) {
        const messages = viewMode === 'customer' ? {
          title: 'Feedback skickad!',
          message: 'Din feedback har skickats till byggkonsulten.',
          type: 'success'
        } : {
          title: 'Svar skickat!',
          message: 'Ditt svar har skickats till kunden. Kunden har fått ett e-postmeddelande.',
          type: 'success'
        };
        
        onSuccess(messages);
      }
      
    } catch (err) {
      console.error('❌ Fel vid skickande av svar:', err);
      if (onError) {
        onError({
          title: 'Fel uppstod',
          message: 'Kunde inte skicka alla svar. Försök igen.',
          type: 'error'
        });
      }
    } finally {
      setSendingResponse(false);
    }
  };

  // Markera som OK (snabbknapp)
  const handleMarkAsOK = async () => {
    setSendingResponse(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Använd rätt endpoint baserat på viewMode
      const endpoint = viewMode === 'customer' 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/customer-reply`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/consultant-response`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response: '✓ Markerad som OK'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte markera som OK');
      }

      const data = await response.json();
      
      if (data.success) {
        // Uppdatera tråden och status
        setFeedbackThread(data.thread);
        await fetchIntelligentStatus();
        
        // Visa toast-notifikation (använder det globala systemet)
        showToast('Kommentar markerad som OK');
      } else {
        throw new Error(data.message || 'Kunde inte markera som OK');
      }
    } catch (err) {
      console.error('❌ Fel vid markering som OK:', err);
      showToast(err.message || 'Kunde inte markera som OK', 'error');
    } finally {
      setSendingResponse(false);
    }
  };

  // Toggle expansion of feedback item
  const toggleFeedbackItem = (itemKey) => {
    setExpandedFeedbackItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  return {
    // State
    consultantResponse,
    sendingResponse,
    expandedFeedbackItems,
    newReplies,
    savedReplies,
    
    // Setters
    setConsultantResponse,
    setExpandedFeedbackItems,
    setNewReplies,
    setSavedReplies,
    
    // Actions
    handleSendConsultantResponse,
    handleSaveReply,
    handleSendAllReplies,
    handleMarkAsOK,
    toggleFeedbackItem,
  };
};

export default useOffertFeedback;

